import { Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { importVenueDatesFromHeeds } from '@/domain/project-venue-dates/actions/import-venue-dates-from-heeds';
import { VenueDateBasic } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { selectProjectVenueIsImportingDates } from '@/domain/project-venue-dates/project-venue-dates.selectors';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSCard } from '@/ui/components/FSCard';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { prepareMinMaxForDates } from '../modal-import-api-utils/prepare-min-max-for-dates';

export function ModalImportApiHeeds({
  open,
  onClose,
  onImportDates,
}: {
  open: boolean;
  onClose: () => void;
  onImportDates: (dates: VenueDateBasic[]) => void;
}) {
  return (
    <FSModal open={open} onClose={onClose} maxWidth={600}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <FSModalTitle
            title={translate('VenueDates.ImportApiHeedsModal.title')}
          />
          <CloseButton onClick={onClose} />
        </Stack>
        <HowToUseMessage />
        <ImportForm
          onImportDates={(dates) => {
            onClose();
            onImportDates(dates);
          }}
        />
      </Stack>
    </FSModal>
  );
}

function HowToUseMessage() {
  return (
    <FSCard
      variant="elevation"
      sx={{ bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2 }}
    >
      <Stack sx={{ p: 2, gap: 2 }}>
        <Typography>
          {
            "Vos identifiants d'accès API sont disponible dans votre manager Heeds."
          }
        </Typography>
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography>Menu</Typography>
          <Chip label="Communication" />
          <Typography>&gt;</Typography>
          <Chip label="Paramètres" />
          <Typography>&gt;</Typography>
          <Chip label="  API" />
        </Stack>
      </Stack>
    </FSCard>
  );
}

type Inputs = {
  username: string;
  password: string;
  startDate: string;
  endDate: string;
};

function ImportForm({
  onImportDates,
}: {
  onImportDates: (dates: VenueDateBasic[]) => void;
}) {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectCurrentProjectVenue);
  const isImportingDates = useAppSelector(selectProjectVenueIsImportingDates);

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      username: '',
      password: '',
      startDate: project?.startDate || '',
      endDate: project?.endDate || '',
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const dates: VenueDateBasic[] = await dispatch(
      importVenueDatesFromHeeds({
        username: data.username,
        password: data.password,
        startDate: data.startDate,
        endDate: data.endDate,
      }),
    ).unwrap();
    onImportDates(dates);
  };

  const { minStartDate, maxStartDate, minEndDate, maxEndDate } =
    prepareMinMaxForDates(
      project?.startDate || '',
      project?.endDate || '',
      watch('startDate'),
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <FSTextField
          label={translate('VenueDates.ImportApiHeedsModal.form.username')}
          type="text"
          register={{
            ...register('username', {
              required: translate('Common.field-is-required'),
            }),
          }}
          error={!!errors.username}
          helperText={errors.username?.message}
        />
        <FSTextField
          label={translate('VenueDates.ImportApiHeedsModal.form.password')}
          type="password"
          register={{
            ...register('password', {
              required: translate('Common.field-is-required'),
            }),
          }}
          error={!!errors.password}
          helperText={errors.password?.message}
        />
        <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Controller
            name={'startDate'}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FSDateField
                label={translate(
                  'VenueDates.ImportApiHeedsModal.form.start-date',
                )}
                value={field.value}
                onChange={field.onChange}
                name={field.name}
                minDate={minStartDate}
                maxDate={maxStartDate}
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
              />
            )}
          />
          <Controller
            name={'endDate'}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FSDateField
                label={translate(
                  'VenueDates.ImportApiHeedsModal.form.end-date',
                )}
                value={field.value}
                onChange={field.onChange}
                name={field.name}
                minDate={minEndDate}
                maxDate={maxEndDate}
                error={!!errors.endDate}
                helperText={errors.endDate?.message}
              />
            )}
          />
        </Stack>
      </Stack>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'right',
          gap: 2,
          mt: 2,
        }}
      >
        {isImportingDates && <CircularProgress size={20} />}
        <FSPrimaryButton type="submit" disabled={isImportingDates}>
          {translate('VenueDates.ImportApiHeedsModal.form.submit')}
        </FSPrimaryButton>
      </Stack>
    </form>
  );
}
